import { FC } from 'react';
import type { Location } from 'react-router-dom';

import { useNavigationConfirm } from '@/global-hooks/use-navigation-confirm';
import { Modal } from '@/shared/design-system';

export const LEAVE_TITLE = 'Leave without saving?';
export const KEEP_EDITING_LABEL = 'Keep editing';
export const DISCARD_AND_LEAVE_LABEL = 'Discard changes';

export const leaveDescription = (roleCount: number) =>
  roleCount === 1
    ? 'The permission changes you have not reviewed yet will be lost.'
    : `The permission changes you have not reviewed yet on ${roleCount} roles will be lost.`;

const tabOf = (location: Location) => new URLSearchParams(location.search).get('tab') ?? '';

/**
 * Moving between roles, tabs and views keeps the query string's `tab` (the Manage Team page's
 * own tab) and the path, so only a change to either one leaves the board and its drafts.
 */
export const leavesRolesBoard = ({
  currentLocation,
  nextLocation,
}: {
  currentLocation: Location;
  nextLocation: Location;
}) =>
  currentLocation.pathname !== nextLocation.pathname ||
  tabOf(currentLocation) !== tabOf(nextLocation);

type UnsavedDraftsGuardProps = {
  /** Roles with a draft that has not been sent yet. */
  draftCount: number;
};

/** Asks before navigation or a page unload drops the board's unsent permission drafts. */
export const UnsavedDraftsGuard: FC<UnsavedDraftsGuardProps> = ({ draftCount }) => {
  const { isBlocked, confirm, cancel } = useNavigationConfirm(draftCount > 0, leavesRolesBoard);

  return (
    <Modal
      open={isBlocked}
      onOpenChange={(isOpen) => {
        if (!isOpen) cancel();
      }}
      variant="center"
      title={LEAVE_TITLE}
      description={leaveDescription(draftCount)}
      primaryAction={{ label: DISCARD_AND_LEAVE_LABEL, color: 'destructive', onClick: confirm }}
      secondaryAction={{ label: KEEP_EDITING_LABEL, onClick: cancel }}
    >
      <></>
    </Modal>
  );
};
